import type { ComponentChildren } from 'preact';
import { CaptureGlyph as Knot } from './CaptureGlyph';
import {
  knotSlotIndex,
  navHref,
  isGoldTab,
  badgeLabel,
  type NavModelProps,
  type NavDestination,
  type TabBadge,
} from './navModel';
import type { MoreSheetExtraEntry } from './MoreSheet';
import type { TopTab } from '../routes/route';
import './SideRail.css';

// R1a — the TABLET/DESKTOP rendering of the SAME nav model the phone
// BottomNav uses (see ../navModel). A fixed left rail: the destinations laid
// out top-to-bottom with the Knot as the central capture action in the same
// middle slot the bottom bar centres it in. Active/gold rule identical to
// the bottom nav (Career is gold; everything else uses the ordinary active
// colour). The rail is pinned full-height; the content column beside it
// owns its own scroll. Core hardening 2026-07-29, Item 8: `destinations` is
// now a prop rather than the fixed NAV_DESTINATIONS module constant.
//
// S6 (Huan's browser walk, 2026-08-11): the rail no longer carries a brand
// glyph — the blessed rails (wf02 §01/§01b, wf04 §01) put the Knot at the
// head with NOTHING above it, and a wordmark in a work rail someone lives
// in all day is furniture. `accountEntry` is the shell's account/settings
// affordance, handed in as a slot and laid out at the FOOT of the rail —
// infrequent, system-level, where people already look for it — instead of
// floating over the rail's head (where it sat on top of the old glyph).
export function SideRail({
  active,
  onNavigate,
  knotState,
  onKnotPress,
  destinations,
  accountEntry,
  badges,
  extra,
}: NavModelProps & { accountEntry?: ComponentChildren; extra?: MoreSheetExtraEntry[] }) {
  const foot = accountEntry ? <div class="side-rail__foot">{accountEntry}</div> : null;
  // B10 §4 — a trailing item for anything that is not one of the rail's own
  // tabs (no `TopTab`, no server-declared `NavDestination`), FROM THE SAME
  // `moreExtra` SOURCE App.tsx already hands MoreSheet — never a second list
  // that could name a different set of screens. Rendered last, after every
  // group and before the account foot, on BOTH layouts below: the existing
  // rail order (P1's own groups, or the knot-split top/bottom halves) is
  // otherwise untouched.
  const extraItems =
    extra && extra.length > 0 ? (
      <div class="side-rail__group" key="extra">
        {extra.map((item) => (
          <a
            key={item.key}
            class="side-rail__item"
            href={item.href}
            onClick={(e) => {
              e.preventDefault();
              item.onNavigate();
            }}
          >
            <span class="side-rail__dot" />
            <span class="side-rail__label">{item.label}</span>
          </a>
        ))}
      </div>
    ) : null;
  // S1 (wf02 §01/§01b): a pack-declared GROUPED rail — the union of every
  // nav-role group the membership holds, rendered as one rail with the
  // Knot at the TOP (the blessed prototype's own rail order) and a quiet
  // separator between groups; group labels appear only when there is more
  // than one group ("Alan holds all three and sees them grouped, in one
  // rail, with no mode to be in" — the label is orientation, not a
  // switcher). Destinations without group data (Actor's defaults, flat
  // navTabs) keep the original centre-Knot layout, byte-identical.
  if (destinations.some((d) => d.group)) {
    const groups: { key: string; label: string; items: NavDestination[] }[] = [];
    for (const item of destinations) {
      const key = item.group ?? '';
      const last = groups[groups.length - 1];
      if (last && last.key === key) last.items.push(item);
      // A3: the pack's own label when it sent one. The key is the fallback
      // for a server that predates groupLabel, and it is a poor one — see
      // NavDestination.groupLabel.
      else groups.push({ key, label: item.groupLabel ?? key, items: [item] });
    }
    const multi = groups.length > 1;
    return (
      <nav class="side-rail" aria-label="Primary">
        <div class="side-rail__knot-slot">
          <button type="button" class="side-rail__knot-btn" aria-label="Capture" onClick={onKnotPress}>
            <Knot state={knotState} />
          </button>
        </div>
        {groups.map((g, i) => (
          <div class="side-rail__group" key={g.key}>
            {i > 0 ? <span class="side-rail__gap" aria-hidden="true" /> : null}
            {multi ? <span class="side-rail__grouplab">{g.label}</span> : null}
            {g.items.map((item) => (
              <RailItem
            key={item.tab}
            item={item}
            active={active === item.tab}
            onNavigate={onNavigate}
            badge={badges?.[item.tab]}
          />
            ))}
          </div>
        ))}
        {extraItems}
        {foot}
      </nav>
    );
  }

  const slot = knotSlotIndex(destinations);
  const top = destinations.slice(0, slot);
  const bottom = destinations.slice(slot);
  return (
    <nav class="side-rail" aria-label="Primary">
      <div class="side-rail__group">
        {top.map((item) => (
          <RailItem
            key={item.tab}
            item={item}
            active={active === item.tab}
            onNavigate={onNavigate}
            badge={badges?.[item.tab]}
          />
        ))}
      </div>
      <div class="side-rail__knot-slot">
        <button type="button" class="side-rail__knot-btn" aria-label="Capture" onClick={onKnotPress}>
          <Knot state={knotState} />
        </button>
      </div>
      <div class="side-rail__group">
        {bottom.map((item) => (
          <RailItem
            key={item.tab}
            item={item}
            active={active === item.tab}
            onNavigate={onNavigate}
            badge={badges?.[item.tab]}
          />
        ))}
      </div>
      {extraItems}
      {foot}
    </nav>
  );
}

function RailItem({
  item,
  active,
  onNavigate,
  badge,
}: {
  item: NavDestination;
  active: boolean;
  onNavigate: (tab: TopTab) => void;
  badge?: TabBadge;
}) {
  const classes = ['side-rail__item'];
  if (active) classes.push(isGoldTab(item.tab) ? 'side-rail__item--gold' : 'side-rail__item--active');
  // CV6b §4 — the same badge the bottom bar draws, off the same model
  // field, so the two renderings cannot disagree about what is new.
  const count = badgeLabel(badge);

  return (
    <a
      class={classes.join(' ')}
      href={navHref(item.tab)}
      aria-current={active ? 'page' : undefined}
      onClick={(e) => {
        e.preventDefault();
        onNavigate(item.tab);
      }}
    >
      <span class="side-rail__dot" />
      <span class="side-rail__label">{item.label}</span>
      {count !== null ? (
        <span class="side-rail__badge" data-testid={`nav-badge-${item.tab}`}>
          {count}
          <span class="side-rail__sr"> unread</span>
        </span>
      ) : null}
    </a>
  );
}
